import React, { useRef, useEffect, useState } from 'react';
import './styles.css';

/**
 * Bayer matrix for ordered dithering (8x8)
 * Creates a consistent pattern that breaks up gradient banding
 */
const BAYER_MATRIX_8x8 = [
  [ 0, 32,  8, 40,  2, 34, 10, 42],
  [48, 16, 56, 24, 50, 18, 58, 26],
  [12, 44,  4, 36, 14, 46,  6, 38],
  [60, 28, 52, 20, 62, 30, 54, 22],
  [ 3, 35, 11, 43,  1, 33,  9, 41],
  [51, 19, 59, 27, 49, 17, 57, 25],
  [15, 47,  7, 39, 13, 45,  5, 37],
  [63, 31, 55, 23, 61, 29, 53, 21]
];

/**
 * Draws a dithered gradient onto a canvas using Bayer matrix ordered dithering.
 * 
 * The key to smooth results is using a very low dither strength - just enough
 * to break up the 8-bit color banding without creating visible patterns.
 * 
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {Object} options - Gradient options
 * @param {number[]} options.color - RGB color [r, g, b]
 * @param {Function} options.alphaFn - Function (x, y, width, height) => alpha (0-1)
 * @param {number} options.ditherStrength - Dithering intensity (default 1.5)
 */
const drawDitheredGradient = (ctx, width, height, options) => {
  const { color = [0, 0, 0], alphaFn, ditherStrength = 1.5 } = options;
  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;
  
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      
      // Get base alpha from the provided function (0-1 range)
      const baseAlpha = alphaFn(x, y, width, height) * 255;
      
      // Bayer matrix ordered dithering with subtle strength
      // Normalize to -0.5 to +0.5 range, then scale by ditherStrength
      const bayerValue = (BAYER_MATRIX_8x8[y % 8][x % 8] / 64.0 - 0.5) * ditherStrength;
      const alpha = Math.max(0, Math.min(255, Math.round(baseAlpha + bayerValue)));
      
      data[i] = color[0];
      data[i + 1] = color[1];
      data[i + 2] = color[2];
      data[i + 3] = alpha;
    }
  }
  
  ctx.putImageData(imageData, 0, 0);
};

/**
 * FokusOverlay Component
 * 
 * Creates a full-screen blue-tinted vignette for FOKUS mode with Bayer matrix dithering.
 * This eliminates the aliasing/banding artifacts that occur with CSS radial-gradient.
 * 
 * Shared across all views: Flow, Kanban, Table, Gantt, TodoList
 * 
 * @param {boolean} isFadingOut - Whether the overlay is fading out (exiting focus mode)
 * @param {boolean} skipEnterAnimation - Skip the enter animation (e.g., when restoring state)
 */
const FokusOverlay = ({ isFadingOut = false, skipEnterAnimation = false }) => {
  const canvasRef = useRef(null);
  // Use ref to track if we've already entered - survives re-renders
  const hasAnimatedRef = useRef(false);
  // Start as false - we'll set to true only on first mount, then immediately schedule removal
  const [isEntering, setIsEntering] = useState(() => {
    // Skip animation if restoring state after tab switch
    if (skipEnterAnimation) return false;
    // Only animate on very first mount
    if (!hasAnimatedRef.current) {
      hasAnimatedRef.current = true;
      return true;
    }
    return false;
  });
  
  // Remove entering class after animation completes (only runs once)
  useEffect(() => {
    if (isEntering) {
      const timer = setTimeout(() => setIsEntering(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isEntering]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const parent = canvas.parentElement;
    const ctx = canvas.getContext('2d');
    const width = parent?.clientWidth || window.innerWidth;
    const height = parent?.clientHeight || window.innerHeight;
    
    canvas.width = width;
    canvas.height = height;
    
    // Blue vignette: stronger at edges, transparent in center
    // Color: rgb(59, 130, 246) - Tailwind blue-500
    const alphaFn = (x, y, w, h) => {
      const centerX = w / 2;
      const centerY = h / 2;
      const dx = (x - centerX) / centerX;
      const dy = (y - centerY) / centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      
      // Vignette: 0 at center, peaks at edges
      const vignette = Math.pow(Math.min(dist, 1.4) / 1.4, 2);
      
      // Max alpha of 0.15 for subtle effect
      return vignette * 0.15;
    };
    
    drawDitheredGradient(ctx, width, height, { 
      color: [59, 130, 246], // Blue
      alphaFn,
      ditherStrength: 1.5  // Subtle - just enough to break banding without visible pattern
    });
    
    const handleResize = () => {
      const newWidth = parent?.clientWidth || window.innerWidth;
      const newHeight = parent?.clientHeight || window.innerHeight;
      canvas.width = newWidth;
      canvas.height = newHeight;
      drawDitheredGradient(ctx, newWidth, newHeight, { 
        color: [59, 130, 246], 
        alphaFn,
        ditherStrength: 1.5
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const overlayClasses = [
    'fokus-overlay',
    isEntering && 'fokus-overlay--entering',
    isFadingOut && 'fokus-overlay--fading-out',
  ].filter(Boolean).join(' ');
  
  return (
    <canvas 
      ref={canvasRef} 
      className={overlayClasses}
    />
  );
};

export default FokusOverlay;
